import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Volume2, WifiOff } from 'lucide-react';
import Waveform from './Waveform';
import './VoiceAssistant.css';

const VoiceAssistant = ({ vehicleData, wssEndpoint = import.meta.env.VITE_WSS_ENDPOINT || 'ws://localhost:5001/ws' }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [audioQueue, setAudioQueue] = useState([]);
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const audioContextRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const sessionIdRef = useRef(null); // Session ID for backend

  // Initialize WebSocket connection
  useEffect(() => {
    connectWebSocket();

    // Cleanup
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      stopListening();
    };
  }, []);

  const connectWebSocket = () => {
    try {
      setConnectionStatus('Connecting...');

      // Generate session ID BEFORE creating WebSocket
      if (!sessionIdRef.current) {
        sessionIdRef.current = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      }

      // Add session_id as URL parameter (backend might expect it here)
      const wsUrl = `${wssEndpoint}?session_id=${sessionIdRef.current}`;
      console.log('Connecting with session_id:', sessionIdRef.current);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected to:', wssEndpoint);
        console.log('Session ID:', sessionIdRef.current);

        // Send initialization message IMMEDIATELY with session_id
        try {
          const initMessage = {
            type: 'init',
            session_id: sessionIdRef.current,
            context: 'vehicle_diagnostics',
            timestamp: Date.now()
          };
          console.log('Sending init message:', initMessage);
          ws.send(JSON.stringify(initMessage));
        } catch (error) {
          console.error('Error sending init message:', error);
        }

        setIsConnected(true);
        setConnectionStatus('Connected');
        reconnectAttemptsRef.current = 0;
        addSystemMessage('🟢 Voice assistant connected. Click microphone to start.');
      };

      ws.onmessage = (event) => {
        console.log('📨 WebSocket message received:', event.data);
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          // If it's not JSON, it might be raw text
          console.log('Raw message:', event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('Error');
        addSystemMessage('❌ Connection error. Retrying...');
      };

      ws.onclose = (event) => {
        console.log('WebSocket disconnected - Code:', event.code, 'Reason:', event.reason, 'Clean:', event.wasClean);
        setIsConnected(false);
        setConnectionStatus('Disconnected');

        // Log close codes for debugging
        const closeReasons = {
          1000: 'Normal closure',
          1001: 'Going away',
          1002: 'Protocol error',
          1003: 'Unsupported data',
          1006: 'Abnormal closure (no status code)',
          1007: 'Invalid frame payload',
          1008: 'Policy violation',
          1009: 'Message too big',
          1010: 'Missing extension',
          1011: 'Internal server error',
          1015: 'TLS handshake failure'
        };
        console.log('Close reason:', closeReasons[event.code] || 'Unknown');

        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            addSystemMessage(`🔄 Reconnecting... (Attempt ${reconnectAttemptsRef.current})`);
            connectWebSocket();
          }, delay);
        } else {
          addSystemMessage('❌ Connection failed. Please refresh the page.');
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      setConnectionStatus('Failed');
      addSystemMessage('❌ Failed to connect to voice service.');
    }
  };

  const handleWebSocketMessage = (data) => {
    console.log('WebSocket message received:', data);

    if (data.type === 'transcript' || data.text) {
      // Display user's transcribed speech
      if (data.is_final !== false) {
        addTranscript('user', data.text);
      }
    }

    if (data.type === 'ai_response' || data.response) {
      // Display AI's text response
      const responseText = data.response || data.text;
      addTranscript('ai', responseText);
    }

    if (data.type === 'audio' || data.audio) {
      // Play AI's audio response
      const audioData = data.audio;
      playAudioResponse(audioData);
    }

    if (data.type === 'error') {
      addSystemMessage(`⚠️ ${data.message || 'An error occurred'}`);
    }
  };

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  const addTranscript = (role, text) => {
    setTranscript(prev => [...prev, {
      role,
      text,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const addSystemMessage = (text) => {
    setTranscript(prev => [...prev, {
      role: 'system',
      text,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const playAudioResponse = async (audioData) => {
    try {
      // Initialize AudioContext if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Decode base64 audio to array buffer
      const binaryString = atob(audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Decode and play audio
      const audioBuffer = await audioContextRef.current.decodeAudioData(bytes.buffer);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      source.start(0);
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const startListening = async () => {
    if (!isConnected) {
      addSystemMessage('⚠️ Voice service not connected. Please check your connection.');
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      addSystemMessage('⚠️ WebSocket not ready. Please wait...');
      return;
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      setIsListening(true);
      addSystemMessage('🎤 Listening...');

      // Create MediaRecorder for audio capture
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;

      // Send audio chunks to WebSocket
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0 && wsRef.current.readyState === WebSocket.OPEN) {
          // Convert blob to base64
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Audio = reader.result.split(',')[1];
            wsRef.current.send(JSON.stringify({
              type: 'audio_chunk',
              audio: base64Audio,
              sample_rate: 16000,
              session_id: sessionIdRef.current
            }));
          };
          reader.readAsDataURL(event.data);
        }
      };

      // Start recording with 100ms chunks
      mediaRecorder.start(100);

    } catch (error) {
      console.error('Microphone access error:', error);
      addSystemMessage('❌ Microphone access denied. Please grant permission.');
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
    }
    setIsListening(false);
    addSystemMessage('🛑 Stopped listening.');
  };

  const handleMicClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <div className="voice-assistant glass-panel">
      {/* Header */}
      <div className="voice-header">
        <div className="voice-title">
          <Volume2 size={24} color="var(--accent-primary)" />
          <h3>Voice Assistant</h3>
        </div>
        <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? (
            <>
              <span className="status-dot pulse"></span>
              <span className="status-text">{connectionStatus}</span>
            </>
          ) : (
            <>
              <WifiOff size={16} />
              <span className="status-text">{connectionStatus}</span>
            </>
          )}
        </div>
      </div>

      {/* Microphone Control */}
      <div className="voice-controls">
        <button
          className={`mic-button ${isListening ? 'listening' : ''} ${!isConnected ? 'disabled' : ''}`}
          onClick={handleMicClick}
          disabled={!isConnected}
        >
          {isListening ? (
            <Mic size={32} color="#ffffff" />
          ) : (
            <MicOff size={32} color={isConnected ? '#94a3b8' : '#64748b'} />
          )}
        </button>

        {isListening && (
          <div className="waveform-wrapper fade-in">
            <Waveform isActive={isListening} />
          </div>
        )}

        {!isListening && isConnected && (
          <p className="voice-hint">Click microphone to ask about battery, range, or diagnostics</p>
        )}
      </div>

      {/* Transcript */}
      <div className="transcript-container">
        <div className="transcript-header">
          <span>Conversation</span>
          {transcript.length > 0 && (
            <button
              className="clear-button"
              onClick={() => setTranscript([])}
            >
              Clear
            </button>
          )}
        </div>

        <div className="transcript-messages">
          {transcript.length === 0 ? (
            <div className="empty-state">
              <Mic size={48} color="var(--text-muted)" />
              <p>No conversation yet</p>
              <span className="empty-hint">Start by clicking the microphone button above</span>
            </div>
          ) : (
            <>
              {transcript.map((entry, i) => (
                <div key={i} className={`message message-${entry.role}`}>
                  <div className="message-header">
                    <span className="message-role">
                      {entry.role === 'user' && '👤 You'}
                      {entry.role === 'ai' && '🤖 AI Assistant'}
                      {entry.role === 'system' && 'ℹ️ System'}
                    </span>
                    <span className="message-time">{entry.timestamp}</span>
                  </div>
                  <div className="message-text">{entry.text}</div>
                </div>
              ))}
              <div ref={transcriptEndRef} />
            </>
          )}
        </div>
      </div>

      {/* Connection Info */}
      <div className="demo-notice">
        <span>🔗 Connected to: {wssEndpoint}</span>
      </div>
    </div>
  );
};

export default VoiceAssistant;
